import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { activoMantenimientoService } from '../../services/activoMantenimiento';
import toast from 'react-hot-toast';
import {
    CheckCircle2,
    AlertTriangle,
    XCircle,
    HelpCircle,
    Upload,
    History,
    ExternalLink,
    FileText,
} from 'lucide-react';

const ESTADO_CONFIG = {
    vigente:    { label: 'Vigente',        icon: CheckCircle2,  cls: 'bg-green-50 text-green-700 border-green-300' },
    por_vencer: { label: 'Vence este mes', icon: AlertTriangle, cls: 'bg-blue-50 text-blue-700 border-blue-300' },
    vencido:    { label: 'Vencido',        icon: XCircle,       cls: 'bg-red-50 text-red-700 border-red-300' },
};

const EstadoBadge = ({ estado }) => {
    const cfg = ESTADO_CONFIG[estado] || { label: 'Sin registro', icon: HelpCircle, cls: 'bg-gray-100 text-gray-500 border-gray-300' };
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`}>
            <Icon className="h-3.5 w-3.5 mr-1" />
            {cfg.label}
        </span>
    );
};

const fmtFecha = (f) => (f ? new Date(f + 'T00:00:00').toLocaleDateString('es-PE') : '—');

const FORM_INICIAL = {
    tipo: '',
    fecha_vencimiento: '',
    fecha_renovacion: '',
    responsable: '',
    costo: '',
    observaciones: '',
};

const ModalMantenimientoActivo = ({ activo, soloLectura = false, onClose }) => {
    const [vigente, setVigente] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [mostrarHistorial, setMostrarHistorial] = useState(false);
    const [mostrarForm, setMostrarForm] = useState(false);
    const [form, setForm] = useState(FORM_INICIAL);
    const [archivo, setArchivo] = useState(null);
    const [guardando, setGuardando] = useState(false);

    const cargar = async () => {
        setCargando(true);
        try {
            const [v, h] = await Promise.all([
                activoMantenimientoService.obtenerVigente(activo.id),
                activoMantenimientoService.historial(activo.id),
            ]);
            setVigente(v);
            setHistorial(h);
        } catch {
            toast.error('Error al cargar el mantenimiento del activo');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activo.id]);

    const handleGuardar = async () => {
        if (!form.fecha_vencimiento) { toast.error('La fecha de vencimiento es obligatoria'); return; }
        setGuardando(true);
        try {
            await activoMantenimientoService.crear(activo.id, form, archivo);
            toast.success('Mantenimiento registrado');
            setForm(FORM_INICIAL);
            setArchivo(null);
            setMostrarForm(false);
            await cargar();
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Error al registrar el mantenimiento');
        } finally {
            setGuardando(false);
        }
    };

    const esImagen = vigente?.content_type?.startsWith('image/');

    return (
        <Modal title={`Mantenimiento / Vencimiento — ${activo.code}`} onClose={onClose} size="md">
            <p className="text-sm text-gray-500 -mt-2 mb-4">{activo.name}</p>

            {cargando ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : (
                <>
                    <div className="border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado actual</span>
                            <EstadoBadge estado={vigente?.estado} />
                        </div>

                        {vigente ? (
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-2">
                                <div>
                                    <span className="text-gray-500">Vence:</span>{' '}
                                    <span className="font-semibold text-gray-800">{fmtFecha(vigente.fecha_vencimiento)}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Última renovación:</span>{' '}
                                    <span className="font-semibold text-gray-800">{fmtFecha(vigente.fecha_renovacion)}</span>
                                </div>
                                {vigente.tipo && (
                                    <div className="col-span-2"><span className="text-gray-500">Tipo:</span> {vigente.tipo}</div>
                                )}
                                {vigente.responsable && (
                                    <div className="col-span-2"><span className="text-gray-500">Responsable:</span> {vigente.responsable}</div>
                                )}
                                {vigente.costo != null && (
                                    <div className="col-span-2"><span className="text-gray-500">Costo:</span> S/ {vigente.costo}</div>
                                )}
                                {vigente.observaciones && (
                                    <div className="col-span-2"><span className="text-gray-500">Obs.:</span> {vigente.observaciones}</div>
                                )}
                                {vigente.archivo_url && (
                                    <div className="col-span-2 mt-1">
                                        <a
                                            href={vigente.archivo_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"
                                        >
                                            {esImagen ? <ExternalLink className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                                            Ver comprobante
                                        </a>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400 mt-1">Este activo no tiene mantenimiento registrado.</p>
                        )}
                    </div>

                    {!soloLectura && (
                        <div className="mb-4">
                            {!mostrarForm ? (
                                <button
                                    onClick={() => setMostrarForm(true)}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <Upload className="h-4 w-4 mr-2" />
                                    Registrar mantenimiento
                                </button>
                            ) : (
                                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                                            Tipo <span className="text-gray-400 font-normal">(opcional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={form.tipo}
                                            onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                                            placeholder="Ej: Recarga de extintor, Calibración PDA, Revisión técnica"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Fecha de vencimiento <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={form.fecha_vencimiento}
                                                onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Fecha de renovación <span className="text-gray-400 font-normal">(opcional)</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={form.fecha_renovacion}
                                                onChange={(e) => setForm({ ...form, fecha_renovacion: e.target.value })}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Responsable / Proveedor <span className="text-gray-400 font-normal">(opcional)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={form.responsable}
                                                onChange={(e) => setForm({ ...form, responsable: e.target.value })}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                Costo (S/) <span className="text-gray-400 font-normal">(opcional)</span>
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={form.costo}
                                                onChange={(e) => setForm({ ...form, costo: e.target.value })}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                                            Observaciones <span className="text-gray-400 font-normal">(opcional)</span>
                                        </label>
                                        <textarea
                                            rows={2}
                                            value={form.observaciones}
                                            onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                                            Comprobante (imagen o PDF) <span className="text-gray-400 font-normal">(opcional)</span>
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*,.pdf,application/pdf"
                                            onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                                            className="w-full text-xs"
                                        />
                                    </div>
                                    <div className="flex gap-2 pt-1">
                                        <button
                                            onClick={handleGuardar}
                                            disabled={guardando}
                                            className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
                                        >
                                            {guardando ? 'Guardando...' : 'Guardar'}
                                        </button>
                                        <button
                                            onClick={() => { setMostrarForm(false); setForm(FORM_INICIAL); setArchivo(null); }}
                                            className="px-4 py-2 border border-gray-300 text-gray-600 text-sm font-semibold rounded-lg hover:bg-gray-50"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        onClick={() => setMostrarHistorial(!mostrarHistorial)}
                        className="flex items-center text-xs font-semibold text-gray-500 hover:text-gray-700"
                    >
                        <History className="h-3.5 w-3.5 mr-1.5" />
                        {mostrarHistorial ? 'Ocultar historial' : `Ver historial (${historial.length})`}
                    </button>

                    {mostrarHistorial && (
                        <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                            {historial.length === 0 && (
                                <p className="text-xs text-gray-400">Sin registros anteriores.</p>
                            )}
                            {historial.map((h) => (
                                <div key={h.id} className={`border rounded-lg p-2.5 text-xs ${h.activo ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200'}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold text-gray-700">{h.tipo || 'Mantenimiento'} — vence {fmtFecha(h.fecha_vencimiento)}</span>
                                        {h.activo && <span className="text-[10px] px-1.5 py-0.5 bg-blue-600 text-white rounded-full font-bold">VIGENTE</span>}
                                    </div>
                                    <p className="text-gray-500 mt-0.5">
                                        Registrado por {h.subido_por || 'N/A'} — {new Date(h.created_at).toLocaleString('es-PE')}
                                    </p>
                                    {h.archivo_url && (
                                        <a href={h.archivo_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                            Ver comprobante
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default ModalMantenimientoActivo;
